import { DatePicker, FormItem, Input, TextArea } from '@lmc/ui-kit'
import { Select, Checkbox, TimePicker } from 'antd'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { Text } from '@lmc/ui-kit/src/general'
import { GoalListResponse } from '@lmc/ts-common'
import dayjs from 'dayjs'
import { IncidentTypeItem } from 'hooks/useIncidentTypeOptions'

type IncidentLogFormFieldsProps = {
  isCase?: boolean
  locations: string[] | undefined | null
  goals: GoalListResponse[] | undefined
  incidentTypes: IncidentTypeItem[]
  hasLocationsEnabled: boolean
}

const IncidentLogFormFields = ({
  isCase,
  locations,
  goals,
  incidentTypes,
  hasLocationsEnabled,
}: IncidentLogFormFieldsProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)

  const goalsOptions = goals?.map((goal) => ({
    value: goal.id,
    label: goal.title,
  }))

  const locationsOptions = locations?.map((location) => ({
    value: location,
    label: location,
  }))

  return (
    <>
      <FormItem
        name={isCase ? ['log', 'item'] : ['item']}
        label={<Text>{t(incidentReportsConstants.INCIDENT_TYPES)}</Text>}
      >
        <Select
          optionRender={(option) => <Text>{option.label}</Text>}
          options={incidentTypes}
        />
      </FormItem>
      <FormItem
        name={isCase ? ['log', 'title'] : 'title'}
        label={<Text>{t(incidentReportsConstants.TITLE)}</Text>}
      >
        <Input maxLength={35} />
      </FormItem>
      <FormItem
        name={isCase ? ['log', 'description'] : 'description'}
        label={<Text>{t(incidentReportsConstants.DESCRIPTION)}</Text>}
      >
        <TextArea
          autoSize={{
            minRows: 3,
          }}
        />
      </FormItem>
      <FormItem
        name={isCase ? ['log', 'dateLogged'] : 'dateLogged'}
        label={<Text>{t(incidentReportsConstants.DATE_LOGGED)}</Text>}
      >
        <DatePicker />
      </FormItem>
      <FormItem
        name={isCase ? ['log', 'timeLogged'] : 'timeLogged'}
        label={<Text>{t(incidentReportsConstants.TIME_LOGGED)}</Text>}
      >
        <TimePicker />
      </FormItem>
      {hasLocationsEnabled && (
        <FormItem
          name={isCase ? ['log', 'location'] : 'location'}
          label={<Text>{t(incidentReportsConstants.LOCATION)}</Text>}
        >
          <Select
            optionRender={(option) => <Text>{option.label}</Text>}
            options={locationsOptions}
          />
        </FormItem>
      )}
      <FormItem
        name={isCase ? ['log', 'goals'] : 'goals'}
        label={<Text>{t(incidentReportsConstants.LINKED_GOALS)}</Text>}
      >
        <Select
          optionRender={(option) => <Text>{option.label}</Text>}
          options={goalsOptions}
          allowClear
          mode="multiple"
        />
      </FormItem>
      <FormItem
        valuePropName="checked"
        name={isCase ? ['log', 'isNextHandover'] : 'isNextHandover'}
        label={<Text>{t(incidentReportsConstants.HANDOVER)}</Text>}
      >
        <Checkbox>
          <Text>{t(incidentReportsConstants.FLAG_FOR_HANDOVER)}</Text>
        </Checkbox>
      </FormItem>
    </>
  )
}

export type IncidentReportsLogFields<T> = {
  goals: string[]
  dateLogged: dayjs.Dayjs
  timeLogged: dayjs.Dayjs
  item?: T
}

export default IncidentLogFormFields
